import { useRef, useState } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { MembershipModal } from 'Components/MembershipModal';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { productsCategories } from 'constants/products';

import { ReactComponent as MembershipQuestionMark } from 'assets/images/active-question-lime.svg';

import { formatPriceWithCurrency } from 'utils/currencies';
import useHeight from 'utils/useHeight';

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-height: 55px;
  margin-bottom: ${legacyTheme.spacing.s32};
  border-top: 1px solid var(--dark-color, ${theme.colors.neutral[400]});
  border-bottom: 1px solid var(--dark-color, ${theme.colors.neutral[400]});

  text-align: center;

  & > * {
    display: flex;
    flex-flow: column;
    justify-content: center;
  }
`;

const PriceWrapper = styled.div`
  flex: 1;
  padding: ${legacyTheme.spacing.s12};
`;

const StyledSpacer = styled(Spacer)`
  flex: 0;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled.button`
  cursor: pointer;

  display: inline-block;

  margin-left: 4px;
  padding: 0;
  border: none;

  background-color: transparent;
`;

type PricingProps = {
  productCategory: typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE;
  currency: string;
  dark?: boolean;
  price: number;
  priceWithSubscription?: number;
  showDiscountModal?: boolean;
};

export const Pricing = ({
  currency,
  dark = false,
  price,
  productCategory,
  priceWithSubscription = 0,
  showDiscountModal = false,
}: PricingProps) => {
  const capacityContainerRef = useRef(null);
  // TODO: type useHeight, which should return 0 by default
  const capacityHeight = useHeight(capacityContainerRef) as unknown as number;
  const [isModalOpen, setIsModalOpen] = useState(false);

  // @Marc: hack to determine which discount the product is elligible to. Should live in the API.
  const elligibleDiscount = (1 - priceWithSubscription / price) * 100;

  return (
    <>
      <MembershipModal
        category={productCategory}
        closeModal={() => setIsModalOpen(false)}
        isModalOpen={isModalOpen}
        triggerTrackingEvent={false}
      />
      <Content style={{ '--dark-color': dark ? theme.colors.neutral[700] : '' }}>
        <PriceWrapper data-testid="products-buy-once-price" style={{}}>
          <Typography markupName="div" medium variant="p2">
            Buy once
          </Typography>

          <Typography markupName="div" variant="p2">
            {formatPriceWithCurrency({ price: price?.toFixed(2), currency })}
          </Typography>
        </PriceWrapper>

        {capacityHeight && (
          <StyledSpacer
            axis="vertical"
            size={Math.max(capacityHeight)}
            style={{
              '--color-background': dark ? theme.colors.neutral[700] : theme.colors.neutral[400],
            }}
          />
        )}

        {Boolean(priceWithSubscription) && (
          <PriceWrapper ref={capacityContainerRef} data-testid="subscribe-price" style={{}}>
            <Typography markupName="div" medium variant="p2">
              Subscribe & save {`${elligibleDiscount.toFixed(0)}%`}
            </Typography>

            <StyledTypography markupName="div" variant="p2">
              {formatPriceWithCurrency({ price: priceWithSubscription?.toFixed(2), currency })}
              {showDiscountModal && (
                <StyledButton
                  data-click="subscription-modal"
                  data-from="pricing-info"
                  onClick={() => setIsModalOpen(true)}
                  type="button"
                >
                  <MembershipQuestionMark style={{ '--fill-color': theme.colors.highlight[200] }} />
                </StyledButton>
              )}
            </StyledTypography>
          </PriceWrapper>
        )}
      </Content>
    </>
  );
};
